import { Auth, Connect, Message, SocketObservable } from '@genesislcap/foundation-comms';
import { DI } from '@microsoft/fast-foundation';
import { INegotiationInsert, IProposalInsert } from '../types';
import { observable } from '@microsoft/fast-element';

export interface INegotiationService {
  negotiationDetailModalVisibility: boolean;
  negotiationDetailModelID: string;
  openNegotiationDetailModal(negotiationId: string) : void;
  closeNegotiationDetailModal() : void;
  getNegotiationConfirmation(negotiationId: string): Promise<Message<any>>;
  getAllNegotiations(params: any): SocketObservable<Message<any>>;
  getNegotiationDetails(criteria?: string): Promise<Message<any>>;
  getNegotiationRFQs(params: any): SocketObservable<Message<any>>;
  getNegotiation(params: any): Promise<Message<any>>;
  getNegotiationVppas(params: any): SocketObservable<Message<any>>;
  getNegotiationDashboardData(params: any): SocketObservable<Message<any>>;
  getAllProposals(negotiationID: string): SocketObservable<Message<any>>;
  eventNegotiationInsert(negotiation: INegotiationInsert): Promise<any>;
  eventProposalInsert(proposal: IProposalInsert): Promise<any>;
  eventNegotiationConfirm(negotiationID: string): Promise<any>;
  eventNegotiationAccept(negotiationID: string): Promise<any>;
  eventNegotiationReject(negotiationID: string): Promise<any>;
  eventReturnToNegotiation(negotiationID: string): Promise<any>;
  eventNegotiationClearReject(negotiationID: string): Promise<any>;
  eventNegotiationExecute(negotiationID: string): Promise<any>;
  eventDeleteExpiredNegotiation(negotiationID: string): Promise<any>;
  eventArteriaReopen(negotiationID: string): Promise<any>;
}

class NegotiationServiceImpl implements INegotiationService {
  @observable public negotiationDetailModalVisibility = false;
  @observable public negotiationDetailModelID: string = null;
  
  constructor(@Connect private connect: Connect, @Auth private auth: Auth) {}

  openNegotiationDetailModal(negotiationId: string) {
    this.negotiationDetailModelID = negotiationId;
    if (this.negotiationDetailModelID) {
      this.negotiationDetailModalVisibility = true;
    }
  }

  closeNegotiationDetailModal() {
    this.negotiationDetailModelID = null;
    this.negotiationDetailModalVisibility = false;
  }

  getNegotiationConfirmation(negotiationId: string): Promise<Message<any>> {
    if (negotiationId) {
      const params = {
        REQUEST: negotiationId,
      };
      return this.connect.request('NEGOTIATION_CONFIRMATION_DATA', params);
    }

    throw new Error('Negotiation id is mandatory!');
  }

  getAllNegotiations(params: any): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'ALL_NEGOTIATIONS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params
    );

    return request;
  }

  getNegotiationDetails(criteria?: string): Promise<Message<any>> {
    return this.connect.snapshot('ALL_NEGOTIATIONS', { CRITERIA_MATCH: criteria });
  }

  getNegotiationDashboardData(params: any): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'NEGOTIATION_DASHBOARD',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params
    );

    return request;
  }

  getNegotiationRFQs(params: any): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'NEGOTIATION_RFQS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params
    );

    return request;
  }

  getNegotiation(params: any): Promise<Message<any>> {
    return this.connect.request('NEGOTIATION', {
      REQUEST: params,
    });
  }

  getNegotiationVppas(params: any): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'NEGOTIATION_VPPAS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params
    );
    return request;
  }

  getAllProposals(negotiationID?: string): SocketObservable<Message<any>> {
    const params = {
      CRITERIA_MATCH: negotiationID ? `NEGOTIATION_ID=='${negotiationID}'` : '',
    };

    const request: any = this.connect.stream(
      'ALL_PROPOSALS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params
    );

    return request;
  }

  async eventNegotiationInsert(negotiation: INegotiationInsert) {
    if (!negotiation) return;

    const request: any = await this.connect.commitEvent('EVENT_NEGOTIATION_INSERT', {
      DETAILS: negotiation,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventProposalInsert(proposal: IProposalInsert) {
    if (!proposal) return;

    const request: any = await this.connect.commitEvent('EVENT_PROPOSAL_INSERT', {
      DETAILS: proposal,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventNegotiationConfirm(negotiationID: string) {
    const request: any = await this.connect.commitEvent('EVENT_NEGOTIATION_CONFIRM', {
      DETAILS: { NEGOTIATION_ID: negotiationID },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventNegotiationAccept(negotiationID: string) {
    const request: any = await this.connect.commitEvent('EVENT_NEGOTIATION_ACCEPT', {
      DETAILS: { NEGOTIATION_ID: negotiationID },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventNegotiationReject(negotiationID: string) {
    const request: any = await this.connect.commitEvent('EVENT_NEGOTIATION_REJECT', {
      DETAILS: { NEGOTIATION_ID: negotiationID },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventReturnToNegotiation(negotiationID: string) {
    const request: any = await this.connect.commitEvent('EVENT_RETURN_TO_NEGOTIATION', {
      DETAILS: { NEGOTIATION_ID: negotiationID },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventNegotiationClearReject(negotiationID: string) {
    const request: any = await this.connect.commitEvent('EVENT_NEGOTIATION_CLEAR_REJECT', {
      DETAILS: { NEGOTIATION_ID: negotiationID },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventNegotiationExecute(negotiationID: string) {
    const request: any = await this.connect.commitEvent('EVENT_NEGOTIATION_EXECUTE', {
      DETAILS: { NEGOTIATION_ID: negotiationID },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventDeleteExpiredNegotiation(negotiationID: string) {
    const request: any = await this.connect.commitEvent('EVENT_NEGOTIATION_DELETE_EXPIRED', {
      DETAILS: { NEGOTIATION_ID: negotiationID },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  async eventArteriaReopen(negotiationID: string) {
    const request: any = await this.connect.commitEvent('EVENT_NEGOTIATION_ARTERIA_REOPEN', {
      DETAILS: negotiationID,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }
}

export const INegotiationService = DI.createInterface<INegotiationService>((x) =>
  x.singleton(NegotiationServiceImpl)
);
